import { Tooltip } from '@signate-cloud/ui-lib/src/components/slims/Tooltip';
import clsx from 'clsx';
import type { ComponentPropsWithRef } from 'react';
import { forwardRef, useState } from 'react';
import type { FieldError } from 'react-hook-form';
import type { FieldValidationError } from 'types/cloud_api_error_type';
import { FormErrorMessage } from '@/components/atoms/FormErrorMessage';

type SignInInputProps = Omit<ComponentPropsWithRef<'input'>, 'className'> & {
  label?: string;
  supplement?: string;
  state?: 'valid' | 'warning' | 'invalid';
  fieldError?: FieldError;
  validationError?: FieldValidationError;
  classNames?: {
    root?: string;
    label?: string;
    supplement?: string;
    input?: string;
  };
};

export const SignInInput = forwardRef<HTMLInputElement, SignInInputProps>(
  (props, ref) => {
    const {
      label,
      supplement,
      state,
      fieldError,
      classNames,
      validationError,
      type,
      ...inputProps
    } = props;

    const [isRevealPassword, setIsRevealPassword] = useState(false);
    const [currentType, setCurrentType] = useState(type);

    const togglePassword = () => {
      const isPassword = currentType === 'password';
      setIsRevealPassword((prevState) => !prevState);
      setCurrentType(isPassword ? 'text' : 'password');
    };

    return (
      <>
        <div className={clsx('c-input-area', classNames?.root)}>
          {label && (
            <h4 className={clsx('c-input-label', classNames?.label)}>
              <label htmlFor={inputProps.id}>{label}</label>
            </h4>
          )}
          <div className={'c-input-box'}>
            <span>
              <input
                ref={ref}
                {...inputProps}
                type={currentType}
                className={clsx(
                  'c-input',
                  state === 'invalid' && 'c-input-is-invalid',
                  type === 'password' && 'c-password-input',
                  classNames?.input
                )}
              />
              {supplement && (
                <small
                  className={clsx(
                    'form-text',
                    'text-muted',
                    classNames?.supplement
                  )}
                >
                  {supplement}
                </small>
              )}
              {type === 'password' && (
                <span
                  onClick={togglePassword}
                  role="presentation"
                  className={'c-password-reveal'}
                >
                  {isRevealPassword ? (
                    <Tooltip
                      toggle="info"
                      placement="top"
                      text="パスワードを非表示にします"
                    >
                      <i className="fas fa-eye-slash" />
                    </Tooltip>
                  ) : (
                    <Tooltip
                      toggle="info"
                      placement="top"
                      text="パスワードを表示します"
                    >
                      <i className="fas fa-eye" />
                    </Tooltip>
                  )}
                </span>
              )}
              <FormErrorMessage formError={fieldError} />
              <FormErrorMessage formError={validationError} />
            </span>
          </div>
        </div>
        <style>
          {`
            .c-input-area {
              display: flex;
              justify-content: space-between;
              margin-bottom: 15px;
            }

            .c-input-label {
              font-size: 14px;
              font-weight: normal;
              width: 150px;
              height: 40px;
              line-height: 40px;
            }

            .c-input-box {
              width: 100%;
              position: relative;
            }

            .c-input {
              width: 100%;
              height: 40px;
              padding: 0 10px;
              font-size: 13px;
              line-height: 38px;
              border: 1px solid #e7e7e7;
              box-sizing: border-box;
              box-shadow: inset 1px 1px 1px 0 rgba(35, 24, 21, .1);
              margin-bottom: 0;
              display: block;
              font-family: inherit;
            }

            .c-input-is-invalid {
              border-color: #dc3545;
            }

            .wd-100p-force { width: 100% !important; }

            .mg-b-30 { margin-bottom: 40px; }

            .c-password-reveal {
              left: auto;
              position: absolute;
              right: 10px;
              top: 10px;
              cursor: pointer;
            }
            .c-password-input {
              padding-right: 30px;
            }
            .tx-75p {
              font-size: 75%;
            }
          `}
        </style>
      </>
    );
  }
);
SignInInput.displayName = '@signate-cloud/sysadmin-app/SignInInput';
